import { useEffect } from "react";
import { Check, Download, FileText, Image as ImageIcon, Loader2, Lock, MoreHorizontal, Printer } from "lucide-react";
import Button from "../ui/Button";
import StatusLabel from "../ui/StatusLabel";
import { classNames } from "../../helpers/classNames";
import CoverPreview from "../builder/CoverPreview";

/**
 * One book on My Books (docs/design/HANDOFF.md, "My Books"): cover, title,
 * status pill (plus "PDF only"), a line of detail, a thin progress bar on
 * drafts, the main action, Download PDF on ordered books with one, and on an
 * ordered book the format it lacks (`other`: "Get it printed" / "Get the PDF",
 * outlined in rose; designs BooksOrdered).
 *
 * Tapping the cover opens the book; on an ordered book it opens the
 * "This book has been ordered" pop-up instead. The ⋯ menu only exists on
 * books not yet ordered. From tablet up it's a dropdown under the ⋯ button
 * (`menu`, the items to show while it's open; TabBooksFormat, DeskBooksFormat);
 * phones get a sheet instead (UserLandingPage).
 */
const STATUS_PILL = { draft: "progress", gift: "progress", ready: "ready", cart: "cart", ordered: "ordered" };

const OTHER_FORMAT = {
  print: { text: "Get it printed", Icon: Printer },
  pdf: { text: "Get the PDF", Icon: FileText },
};

const BookCard = ({ book, info, coverUrl, onOpen, onAction, onMore, menu = null, onCloseMenu = () => {}, download = null, other = null, className }) => {
  const title = book.title || "Untitled book";
  // Escape closes the dropdown.
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCloseMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu, onCloseMenu]);
  const ordered = info.status === "ordered";
  const primary = info.status === "draft" || info.status === "ready" || info.status === "gift";
  return (
    <div className={classNames("flex gap-3.5 rounded-[14px] border border-md-pink-light-1 bg-white p-3 md:flex-col md:gap-3 md:rounded-[18px] md:p-4", className)}>
      <button
        type="button"
        aria-label={`Open ${title}`}
        onClick={onOpen}
        className="relative w-[72px] flex-shrink-0 self-start overflow-hidden rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink md:w-full md:rounded-lg"
      >
        {/* The cover as it prints; before it's drawn, an image icon stands in for the drawing. */}
        <CoverPreview
          imageUrl={coverUrl}
          title={book.title}
          placeholder={
            <span className="flex h-full items-center justify-center text-[#8A7F78]">
              <ImageIcon className="h-6 w-6" aria-hidden="true" />
            </span>
          }
        />
        {ordered ? (
          <span className="absolute right-1 top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[rgba(27,13,18,0.7)] text-white">
            <Lock className="h-3 w-3" aria-hidden="true" />
          </span>
        ) : null}
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="relative flex min-h-[22px] items-start justify-between gap-1">
          <span className="line-clamp-2 text-base font-bold leading-snug text-md-dark md:text-[17px]">{title}</span>
          {!ordered && onMore ? (
            <button
              type="button"
              aria-label={`More options for ${title}`}
              aria-expanded={menu ? true : undefined}
              onClick={onMore}
              className="-mr-2 -mt-1.5 flex h-9 w-11 flex-shrink-0 items-center justify-center rounded-md text-md-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
            >
              <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
          {menu ? (
            <>
              {/* A click anywhere else closes it. */}
              <button type="button" aria-label="Close menu" onClick={onCloseMenu} className="fixed inset-0 z-20 hidden cursor-default md:block" />
              <div role="menu" aria-label={`Options for ${title}`} className="absolute right-0 top-8 z-30 hidden w-[270px] rounded-[14px] border border-md-pink-light-1 bg-white p-1.5 shadow-[0_12px_30px_rgba(27,13,18,0.18)] md:block">
                {menu}
              </div>
            </>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <StatusLabel status={STATUS_PILL[info.status] || "progress"}>{info.label}</StatusLabel>
          {info.isPdf ? <StatusLabel status="pdf" /> : null}
        </div>
        <span className="text-[13px] text-md-muted">{info.meta}</span>
        {info.status === "draft" ? (
          <div className="h-1 overflow-hidden rounded-sm bg-md-pink-light-1" aria-hidden="true">
            <div className="h-full bg-md-pink" style={{ width: `${info.pct}%` }} />
          </div>
        ) : null}
        <div className="mt-0.5 flex flex-wrap gap-2 md:mt-auto md:pt-1">
          {info.action ? (
            <Button variant={primary ? "primary" : "secondary"} size="sm" className="!rounded-[10px] !px-4 !text-sm" onClick={onAction}>
              {info.action}
            </Button>
          ) : null}
          {download ? (
            <Button
              variant="secondary"
              size="sm"
              className={classNames(
                "!rounded-[10px] !px-3.5 !text-sm",
                download.state === "busy" && "!border-[#E5C5CB] !bg-[#FBF4F5] !text-md-pink-darker",
                download.state === "done" && "!border-green-200 !bg-green-50 !text-green-800"
              )}
              onClick={download.onClick}
              busy={download.state === "busy"}
              icon={download.state === "done" ? <Check className="h-4 w-4" aria-hidden="true" /> : download.state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            >
              {download.state === "busy" ? "Preparing PDF" : download.state === "done" ? "Downloaded" : "Download PDF"}
            </Button>
          ) : null}
          {other && OTHER_FORMAT[other.format] ? (() => {
            const { text, Icon } = OTHER_FORMAT[other.format];
            return (
              <Button
                variant="secondary"
                size="sm"
                className="!rounded-[10px] !border-[#E5C5CB] !px-3.5 !text-sm !text-md-pink-dark"
                onClick={other.onClick}
                busy={other.busy}
                icon={<Icon className="h-4 w-4" aria-hidden="true" />}
              >
                {text}
              </Button>
            );
          })() : null}
        </div>
      </div>
    </div>
  );
};

export default BookCard;
